<script setup>
import { ref, watch, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAutomation } from 'dashboard/composables/useAutomation';
import { useEditableAutomation } from 'dashboard/composables/useEditableAutomation';
import AutomationRuleForm from './AutomationRuleForm.vue';
import PromptsAPI from 'dashboard/api/prompts';
import {
  AUTOMATION_ACTION_TYPES,
  CUSTOM_TEMPLATE_ACTION_PREFIX,
  buildCustomTemplateActionTypes,
} from './constants';

const props = defineProps({
  selectedResponse: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['saveAutomation']);

const allCustomAttributes = useMapGetter('attributes/getAttributes');
const formRef = ref(null);

const {
  automation,
  automationTypes,
  onEventChange,
  getConditionDropdownValues,
  appendNewCondition,
  appendNewAction,
  removeFilter,
  removeAction,
  resetAction,
  getActionDropdownValues,
  manifestCustomAttributes,
} = useAutomation();

const { formatAutomation } = useEditableAutomation();

const customTemplateNames = ref([]);

const fetchCustomTemplates = async () => {
  try {
    const response = await PromptsAPI.getCustomTemplates();
    customTemplateNames.value = response.data || [];
  } catch {
    // Silently fail
  }
};

onMounted(fetchCustomTemplates);

const expandCustomTemplateActions = value => {
  if (!value?.actions) return value;
  return {
    ...value,
    actions: value.actions.map(action => {
      if (action.action_name === 'send_custom_email_template') {
        const templateName = action.action_params?.[0] || '';
        const email = action.action_params?.[1] || '';
        return {
          ...action,
          action_name: `${CUSTOM_TEMPLATE_ACTION_PREFIX}${templateName}`,
          action_params: email ? [email] : [],
        };
      }
      return action;
    }),
  };
};

const loadFromSelected = () => {
  const value = props.selectedResponse;
  if (!value?.conditions) return;

  manifestCustomAttributes();

  const expandedValue = expandCustomTemplateActions(value);
  const customActionTypes = buildCustomTemplateActionTypes(
    customTemplateNames.value
  );
  const allActionTypes = [...AUTOMATION_ACTION_TYPES, ...customActionTypes];

  automation.value = formatAutomation(
    expandedValue,
    allCustomAttributes.value,
    automationTypes,
    allActionTypes
  );
};

const open = () => formRef.value?.open();
const dismiss = () => formRef.value?.dismiss();
const cancel = () => {
  loadFromSelected();
  formRef.value?.cancel();
};
const resetAndClose = () => {
  loadFromSelected();
  formRef.value?.dismiss();
};

const onSave = (payload, mode) => {
  emit('saveAutomation', payload, mode);
};

const onCancel = () => {
  loadFromSelected();
};

watch(
  () => props.selectedResponse?.id,
  (newId, oldId) => {
    if (!newId) return;
    if (newId !== oldId) {
      loadFromSelected();
    } else if (!automation.value?.conditions) {
      loadFromSelected();
    }
  },
  { immediate: true }
);

defineExpose({ open, close: dismiss, dismiss, cancel, resetAndClose });
</script>

<template>
  <AutomationRuleForm
    ref="formRef"
    v-model:automation="automation"
    mode="edit"
    :automation-types="automationTypes"
    :get-condition-dropdown-values="getConditionDropdownValues"
    :get-action-dropdown-values="getActionDropdownValues"
    :append-new-condition="appendNewCondition"
    :append-new-action="appendNewAction"
    :remove-filter="removeFilter"
    :remove-action="removeAction"
    :reset-action="resetAction"
    :on-event-change="onEventChange"
    @save="onSave"
    @cancel="onCancel"
  />
</template>
